import * as React from "react";
import type {
  FundingPressureBacktestModel,
  FundingPressureForecastPoint,
  FundingPressureSliceResult,
} from "../../models/fundingPressure";

export interface FundingPressurePanelProps {
  result: FundingPressureSliceResult;
  mode: "monitor" | "forecast";
}

function money(value: number) {
  return `${value < 0 ? "-" : ""}$${Math.abs(value).toFixed(1)}B`;
}

function percent(value: number, digits = 1) {
  return `${(value * 100).toFixed(digits)}%`;
}

function modelLabel(model: FundingPressureSliceResult["forecast"]["selectedModel"]) {
  const labels: Record<FundingPressureSliceResult["forecast"]["selectedModel"], string> = {
    "lag-1": "上一季度基线（lag-1）",
    "seasonal-lag-4": "去年同季度基线（seasonal lag-4）",
    "regime-blend": "AI 投资周期趋势组合",
    "seasonal-log-trend": "对数趋势 + 季度季节性",
  };
  return labels[model];
}

function MethodDisclosure({ result }: { result: FundingPressureSliceResult }) {
  return (
    <details className="model-disclosure">
      <summary>方法、回测与来源</summary>
      <div className="method-grid">
        <div><span>目标</span><p>{result.methodology.target}</p></div>
        <div><span>Vintage</span><p>{result.methodology.vintagePolicy}</p></div>
        <div><span>季节性</span><p>{result.methodology.seasonality}</p></div>
        <div><span>rolling-origin</span><p>{result.methodology.validation}</p></div>
        <div><span>模型晋级</span><p>{result.methodology.selection}</p></div>
        <div><span>区间</span><p>{result.methodology.interval}</p></div>
        <div><span>穿越定义</span><p>{result.methodology.crossing}</p></div>
        <div><span>情景传导</span><p>{result.methodology.scenario}</p></div>
      </div>
      <ul>
        {result.sources.map((source) => (
          <li key={source.id}>
            <a href={source.url} target="_blank" rel="noreferrer">{source.label}</a>
            {` · ${source.role}`}
          </li>
        ))}
      </ul>
    </details>
  );
}

function MonitorView({ result }: { result: FundingPressureSliceResult }) {
  const monitor = result.monitor;
  return (
    <section className="sheet funding-pressure-panel" aria-labelledby="funding-pressure-monitor-heading">
      <div className="sheet-heading">
        <div>
          <div className="section-kicker">FUNDING PRESSURE / MONITOR</div>
          <h2 id="funding-pressure-monitor-heading">现金流剪刀差：公司明细</h2>
          <p>把五家公司同一季度的经营现金流与现金资本开支对齐；低于 1.00× 表示内部现金仍能覆盖投入。</p>
        </div>
        <div className="forecast-summary">
          <span>资金覆盖率 · {monitor.commonPeriod}</span>
          <strong>{percent(monitor.aggregate.fundingCoveragePct / 100)}</strong>
          <small>OCF {money(monitor.aggregate.operatingCashFlow)} / CapEx {money(monitor.aggregate.cashCapex)}</small>
          <b>最新 FCF {money(monitor.aggregate.freeCashFlow)}</b>
        </div>
      </div>
      <section className="diagnostic-strip" aria-label="资金压力最新摘要">
        <div><span>CapEx / OCF</span><strong>{monitor.aggregate.capexToOcf.toFixed(2)}×</strong><small>{monitor.aggregate.fundingState === "self-funded" ? "经营现金覆盖" : "存在融资缺口"}</small></div>
        <div><span>最新 FCF</span><strong>{money(monitor.aggregate.freeCashFlow)}</strong><small>{monitor.commonPeriod}</small></div>
        <div><span>现金 CapEx</span><strong>{money(monitor.aggregate.cashCapex)}</strong><small>{monitor.companies.length} 家对齐合计</small></div>
        <div><span>OCF</span><strong>{money(monitor.aggregate.operatingCashFlow)}</strong><small>共同季度</small></div>
      </section>
      <div className="table-shell">
        <table className="research-table company-table">
          <caption className="sr-only">各公司资金覆盖率、FCF 与数据水位</caption>
          <thead>
            <tr><th>公司</th><th>现金 CapEx</th><th>OCF</th><th>CapEx / OCF</th><th>FCF</th><th>可用水位</th></tr>
          </thead>
          <tbody>
            {monitor.companies.map((company) => (
              <tr key={company.id}>
                <td><strong>{company.name}</strong><small>{company.fundingState === "self-funded" ? "自筹覆盖" : `缺口 ${money(company.financingGap)}`}</small></td>
                <td>{money(company.cashCapex)}</td>
                <td>{money(company.operatingCashFlow)}</td>
                <td>{company.capexToOcf.toFixed(2)}×</td>
                <td>{money(company.freeCashFlow)}</td>
                <td>{company.watermark.availableAt}<small>{company.watermark.ageDays} 天</small></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {monitor.benchmarkCrossCheck && (
        <p className="data-caveat">
          Epoch 五公司聚合交叉验证 · {monitor.benchmarkCrossCheck.warning}
        </p>
      )}
      <MethodDisclosure result={result} />
    </section>
  );
}

function HorizonCard({ label, point }: { label: string; point: FundingPressureForecastPoint }) {
  return (
    <div>
      <span>{label} · {point.period}</span>
      <strong>{point.capexToOcf.toFixed(2)}×</strong>
      <small>FCF {money(point.freeCashFlow)} · {point.ratioInterval.label}</small>
    </div>
  );
}

function BacktestRow({ model }: { model: FundingPressureBacktestModel }) {
  return (
    <tr>
      <td><strong>{model.label}</strong><small>{model.metrics.full.observations} 个 OOS 季度</small></td>
      <td>{model.metrics.full.ratioRmse.toFixed(3)}</td>
      <td>{model.metrics.recent8.ratioRmse.toFixed(3)}</td>
      <td>{model.metrics.multiHorizon8.compositeScore.toFixed(3)}</td>
      <td>{money(model.metrics.full.fcfRmse)}</td>
      <td>{money(model.metrics.recent8.fcfRmse)}</td>
    </tr>
  );
}

function ForecastView({ result }: { result: FundingPressureSliceResult }) {
  const forecast = result.forecast;
  const crossing = forecast.crossing;
  const crossingLabel = crossing.status === "censored"
    ? `截至 ${crossing.censoredAfter} 未穿越`
    : crossing.status === "already-crossed" ? `已于 ${crossing.pointEstimate} 穿越` : `点路径 ${crossing.pointEstimate}`;
  return (
    <section className="sheet funding-pressure-panel" aria-labelledby="funding-pressure-forecast-heading">
      <div className="sheet-heading">
        <div>
          <div className="section-kicker">FUNDING PRESSURE / FORECAST</div>
          <h2 id="funding-pressure-forecast-heading">现金流剪刀差：模型依据</h2>
          <p>当前选中 {modelLabel(forecast.selectedModel)}；未来假设只改变预测路径，历史逐季回测保持不变。</p>
        </div>
        <div className="forecast-summary">
          <span>8Q 内穿越风险</span>
          <strong>{crossing.calibrated && crossing.probabilityWithinHorizon != null
            ? percent(crossing.probabilityWithinHorizon)
            : "未校准"}</strong>
          <small>{crossingLabel}</small>
          <b>{crossing.calibrated && crossing.conditionalQuarterInterval90
            ? `条件区间 ${crossing.conditionalQuarterInterval90.join(" — ")}`
            : `${crossing.residualBlocks} 条完整 8Q 样本外残差路径`}</b>
        </div>
      </div>
      <section className="diagnostic-strip" aria-label="资金压力预测关键季度">
        <HorizonCard label="1Q" point={forecast.horizons.quarter1} />
        <HorizonCard label="4Q" point={forecast.horizons.quarter4} />
        <HorizonCard label="8Q" point={forecast.horizons.quarter8} />
        <div><span>Scenario</span><strong>{forecast.scenarioImpact.affectsCurrentChart ? "已启用" : "Baseline"}</strong><small>仅影响未来 CapEx / OCF</small></div>
      </section>
      <div className="table-shell">
        <table className="research-table company-table">
          <caption className="sr-only">资金压力模型全窗与近期八季度回测</caption>
          <thead>
            <tr><th>rolling-origin 模型</th><th>全窗 Ratio RMSE</th><th>近期 8Q Ratio RMSE</th><th>1–8Q 联合损失</th><th>全窗 FCF RMSE</th><th>近期 8Q FCF RMSE</th></tr>
          </thead>
          <tbody>
            <BacktestRow model={result.backtest.models.seasonalLag4} />
            <BacktestRow model={result.backtest.models.seasonalLogTrend} />
            <BacktestRow model={result.backtest.models.regimeBlend} />
            <BacktestRow model={result.backtest.models.lag1} />
            <BacktestRow model={result.backtest.models.epochLogLinear} />
          </tbody>
        </table>
      </div>
      <p className="data-caveat">
        {crossing.explanation} 90% 统计区间与 baseline-to-current scenario range 分开计算。
      </p>
      {forecast.externalBenchmark && (
        <p className="data-caveat">
          Epoch trend 仅作 cross-check · {forecast.externalBenchmark.warning}
        </p>
      )}
      <MethodDisclosure result={result} />
    </section>
  );
}

/** Embeddable content for the product's existing Monitor and Forecast tabs. */
export function FundingPressurePanel({ result, mode }: FundingPressurePanelProps) {
  return mode === "monitor" ? <MonitorView result={result} /> : <ForecastView result={result} />;
}
